import React, { useMemo } from 'react';
import { PiChartBarLight } from 'react-icons/pi';
import type { WebsiteAnalyticsResponse } from 'src/api/generated';
import Loading from 'src/modules/shared/components/atoms/loading';
import { PropertyCode } from 'src/modules/shared/components/atoms/property-code';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { Text } from 'src/modules/shared/components/atoms/text';
import { InlineFormSection } from 'src/modules/shared/components/molecules/inline-form-section/inline-form-section';
import { useReferencedProperties } from 'src/modules/shared/hooks/api/catalog/use-referenced-properties';
import { formatDate } from 'src/modules/shared/utils/formatting/date.util';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { TranslateParams } from 'src/modules/shared/types/i18n/i18n.type';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import { WebsiteAnalyticsAlerts } from '../molecules/website-analytics-alerts';

type WebsiteAnalyticsTranslate = (
  key: TranslationKey,
  params?: TranslateParams
) => string;

const ANALYTICS_SLOT_LABEL_KEYS: Partial<Record<string, TranslationKey>> = {
  generalContact: 'websites.settings.whatsApp.cta.generalContact.label',
  propertyCard: 'websites.settings.whatsApp.cta.propertyCard.label',
  propertyDetail: 'websites.settings.whatsApp.cta.propertyDetail.label',
  scheduleVisit: 'websites.settings.whatsApp.cta.scheduleVisit.label',
  aiHandoff: 'websites.settings.whatsApp.cta.aiHandoff.label',
};

const getAnalyticsSlotLabel = (
  t: WebsiteAnalyticsTranslate,
  slot: string
): string => {
  const labelKey = ANALYTICS_SLOT_LABEL_KEYS[slot];
  return labelKey ? t(labelKey) : slot;
};

/** Props for the WhatsApp analytics panel. */
interface WebsiteAnalyticsPanelProps {
  /** Click analytics for the last 30 days, or `null` before the first publication. */
  analytics: WebsiteAnalyticsResponse | null;
  /** Whether the analytics request is running. */
  loading: boolean;
  /** Whether the analytics request failed. */
  hasError: boolean;
}

/**
 * Shows the last 30 days of WhatsApp clicks from the public site: totals, the
 * best call to action, daily activity, top listings, and recent clicks.
 *
 * @param props - Analytics data and request state.
 * @returns The analytics panel.
 */
export const WebsiteAnalyticsPanel: React.FC<WebsiteAnalyticsPanelProps> = ({
  analytics,
  loading,
  hasError,
}) => {
  const { t } = useI18n();
  const { properties, loadingPropertyIds } = useReferencedProperties([
    ...(analytics?.topProperties ?? []).map((property) => property.propertyId),
    ...(analytics?.recentClicks.slice(0, 8) ?? []).map(
      (event) => event.propertyId
    ),
  ]);
  const recentDailyClicks = useMemo(
    () => analytics?.dailyClicks.slice(-14) ?? [],
    [analytics?.dailyClicks]
  );

  const maxDailyClicks = useMemo(
    () =>
      recentDailyClicks.reduce(
        (currentMax, item) => Math.max(currentMax, item.clicks),
        0
      ) || 1,
    [recentDailyClicks]
  );

  const topAnalyticsSlotLabel = useMemo(() => {
    if (!analytics || analytics.totalClicks === 0) {
      return t('websites.settings.analytics.emptyValue');
    }

    const topSlot = [...analytics.slotBreakdown].sort(
      (left, right) => right.clicks - left.clicks
    )[0];

    return getAnalyticsSlotLabel(t, topSlot.slot);
  }, [analytics, t]);

  return (
    <InlineFormSection
      title={t('websites.settings.analytics.title')}
      description={t('websites.settings.analytics.description')}
      icon={<PiChartBarLight className="h-5 w-5" aria-hidden />}
      headerAction={
        analytics ? (
          <Text size="tiny" color="secondary">
            {t('websites.settings.analytics.clicksCount', {
              count: analytics.totalClicks,
            })}
          </Text>
        ) : undefined
      }
    >
      {loading ? <Loading /> : null}

      {!loading && hasError ? (
        <ErrorState
          title={t('websites.settings.analytics.error.title')}
          description={t('websites.settings.analytics.error.description')}
        />
      ) : null}

      {!loading && !hasError && !analytics ? (
        <Text size="tiny" color="secondary">
          {t('websites.settings.analytics.empty.description')}
        </Text>
      ) : null}

      {!loading && analytics ? (
        <div className="space-y-4">
          <WebsiteAnalyticsAlerts alerts={analytics.alerts} />

          <div className="grid gap-3 md:grid-cols-3">
            <div className="rounded-lg bg-neutral-50 p-4">
              <Text size="tiny" color="secondary">
                {t('websites.settings.analytics.totalClicks')}
              </Text>
              <Text as="p" size="h2" weight="medium">
                {analytics.totalClicks}
              </Text>
            </div>
            <div className="rounded-lg bg-neutral-50 p-4">
              <Text size="tiny" color="secondary">
                {t('websites.settings.analytics.bestCta')}
              </Text>
              <Text as="p" size="h3" weight="medium">
                {topAnalyticsSlotLabel}
              </Text>
            </div>
            <div className="rounded-lg bg-neutral-50 p-4">
              <Text size="tiny" color="secondary">
                {t('websites.settings.analytics.propertiesConsulted')}
              </Text>
              <Text as="p" size="h3" weight="medium">
                {analytics.topProperties.length}
              </Text>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-3 rounded-lg border border-neutral-200 p-4">
              <Text weight="medium">
                {t('websites.settings.analytics.ctaBySlot')}
              </Text>
              {analytics.slotBreakdown.map((slot) => (
                <div
                  key={slot.slot}
                  className="flex items-center justify-between gap-4"
                >
                  <Text size="tiny" color="secondary">
                    {getAnalyticsSlotLabel(t, slot.slot)}
                  </Text>
                  <Text size="tiny">{slot.clicks}</Text>
                </div>
              ))}
            </div>

            <div className="space-y-3 rounded-lg border border-neutral-200 p-4">
              <Text weight="medium">
                {t('websites.settings.analytics.dailyActivity')}
              </Text>
              {recentDailyClicks.length === 0 ? (
                <Text size="tiny" color="secondary">
                  {t('websites.settings.analytics.dailyActivity.empty')}
                </Text>
              ) : (
                recentDailyClicks.map((item) => (
                  <div
                    key={item.date}
                    className="grid grid-cols-[72px_minmax(0,1fr)_32px] items-center gap-3"
                  >
                    <Text size="tiny" color="secondary">
                      {item.date.slice(5)}
                    </Text>
                    <div className="h-2 rounded-full bg-neutral-100">
                      <div
                        className="h-2 rounded-full bg-info-500"
                        style={{
                          width: `${Math.max(
                            (item.clicks / maxDailyClicks) * 100,
                            item.clicks > 0 ? 8 : 0
                          )}%`,
                        }}
                      />
                    </div>
                    <Text size="tiny">{item.clicks}</Text>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-3 rounded-lg border border-neutral-200 p-4">
              <Text weight="medium">
                {t('websites.settings.analytics.topProperties')}
              </Text>
              {analytics.topProperties.length === 0 ? (
                <Text size="tiny" color="secondary">
                  {t('websites.settings.analytics.topProperties.empty')}
                </Text>
              ) : (
                analytics.topProperties.map((property) => (
                  <div
                    key={property.propertyId}
                    className="flex items-center justify-between gap-4"
                  >
                    <div>
                      <Text size="tiny" weight="medium">
                        {property.propertyName ??
                          t('websites.settings.analytics.propertyFallback', {
                            id: property.propertyId,
                          })}
                      </Text>
                      <Text size="tiny" color="secondary">
                        <PropertyCode
                          loading={loadingPropertyIds.has(property.propertyId)}
                          displayCode={
                            properties.get(property.propertyId)?.displayCode
                          }
                          fallback={
                            property.propertyCode ?? `ID ${property.propertyId}`
                          }
                        />
                      </Text>
                    </div>
                    <Text size="tiny">{property.clicks}</Text>
                  </div>
                ))
              )}
            </div>

            <div className="space-y-3 rounded-lg border border-neutral-200 p-4">
              <Text weight="medium">
                {t('websites.settings.analytics.recentClicks')}
              </Text>
              {analytics.recentClicks.length === 0 ? (
                <Text size="tiny" color="secondary">
                  {t('websites.settings.analytics.recentClicks.empty')}
                </Text>
              ) : (
                analytics.recentClicks.slice(0, 8).map((event, index) => {
                  const displayCode =
                    event.propertyId == null
                      ? undefined
                      : properties.get(event.propertyId)?.displayCode;
                  return (
                    <div
                      key={`${event.createdAt}-${index}`}
                      className="space-y-1"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <Text size="tiny" weight="medium">
                          {getAnalyticsSlotLabel(t, event.slot)}
                        </Text>
                        <Text size="tiny" color="secondary">
                          {formatDate(event.createdAt)}
                        </Text>
                      </div>
                      <Text size="tiny" color="secondary">
                        {event.propertyName
                          ? `${event.propertyName}${displayCode || event.propertyCode ? ' · ' : ''}`
                          : ''}
                        <PropertyCode
                          loading={
                            event.propertyId != null &&
                            loadingPropertyIds.has(event.propertyId)
                          }
                          displayCode={displayCode}
                          fallback={
                            event.propertyCode ??
                            (event.propertyName
                              ? ''
                              : event.pageUrl ??
                                t(
                                  'websites.settings.analytics.recentClicks.noProperty'
                                ))
                          }
                        />
                      </Text>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      ) : null}
    </InlineFormSection>
  );
};
